<template>
    <div class="tw-w-full tw-h-full tw-bg-white tw-rounded-lg tw-p-6 tw-border tw-border-solid tw-border-primary-200">
        <!-- Header Section -->
        <div class="tw-flex tw-items-center tw-justify-between tw-mb-8">
            <div>
                <h1 class="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2">Create New Product</h1>
                <p class="tw-text-sm tw-text-gray-600">Add a new product to your collection</p>
            </div>
            
            <!-- Back Button -->
            <a-button
                @click="goBack"
                intent="light"
                left-icon="solar:arrow-left-linear"
            >
                Back to Products
            </a-button>
        </div>

        <!-- Form Content -->
        <form @submit.prevent="saveProduct" class="tw-max-w-4xl">
            <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-8">
                <!-- Left Column - Product Information -->
                <div class="tw-space-y-6">
                    <div>
                        <h2 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-4">Product Information</h2>
                        
                        <!-- Product Title -->
                        <div class="tw-mb-6">
                            <FormField
                                v-model="productForm.title"
                                type="text"
                                label="Product Title"
                                placeholder="Enter product title"
                                left-icon="solar:box-linear"
                                :error="productErrors.title"
                                required
                                help="Enter a clear, descriptive title for your product"
                            />
                        </div>

                        <!-- Product Description -->
                        <div class="tw-mb-6">
                            <FormField
                                v-model="productForm.description"
                                type="textarea"
                                label="Description"
                                placeholder="Enter detailed product description"
                                :error="productErrors.description"
                                rows="6"
                                help="Provide a detailed description of your product features and benefits"
                            />
                        </div>

                        <!-- Categories -->
                        <div class="tw-mb-6">
                            <!-- Skeleton Loader for Categories -->
                            <div v-if="categoriesLoading" class="tw-space-y-3">
                                <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                                    Categories
                                </label>
                                <div class="tw-relative">
                                    <!-- Skeleton Button -->
                                    <div class="tw-w-full tw-h-[40px] tw-bg-gray-200 tw-rounded-md tw-animate-pulse tw-flex tw-items-center tw-justify-between tw-px-4">
                                        <div class="tw-flex tw-items-center tw-gap-3 tw-flex-1">
                                            <div class="tw-w-5 tw-h-5 tw-bg-gray-300 tw-rounded tw-animate-pulse"></div>
                                            <div class="tw-h-4 tw-bg-gray-300 tw-rounded tw-w-48 tw-animate-pulse"></div>
                                        </div>
                                        <div class="tw-w-5 tw-h-5 tw-bg-gray-300 tw-rounded tw-animate-pulse"></div>
                                    </div>
                                </div>
                                <p class="tw-text-xs tw-text-gray-500">
                                    Choose relevant categories to help customers find your product
                                </p>
                            </div>
                            
                            <!-- Actual FormMultiSelect -->
                            <FormMultiSelect
                                v-else
                                v-model="productForm.categories"
                                :options="categoryOptions"
                                label="Categories"
                                placeholder="Select product categories"
                                left-icon="solar:widget-bold-duotone"
                                :error="productErrors.categories"
                                help="Choose relevant categories to help customers find your product"
                            />
                        </div>

                        <!-- Additional Product Details -->
                        <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4">
                            <!-- Price (optional field for future use) -->
                            <FormField
                                v-if="false"
                                v-model="productForm.price"
                                type="number"
                                label="Price"
                                placeholder="0.00"
                                left-icon="solar:dollar-minimalistic-linear"
                                :error="productErrors.price"
                                step="0.01"
                                min="0"
                            />

                            <!-- SKU (optional field for future use) -->
                            <FormField
                                v-if="false"
                                v-model="productForm.sku"
                                type="text"
                                label="SKU"
                                placeholder="Product SKU"
                                left-icon="solar:tag-linear"
                                :error="productErrors.sku"
                            />
                        </div>
                    </div>
                </div>

                <!-- Right Column - Product Image -->
                <div class="tw-space-y-6">
                    <div>
                        <h2 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-4">Product Image</h2>
                        
                        <!-- Image Upload Area -->
                        <div class="tw-mb-6">
                            <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                                Product Image <span class="tw-text-gray-500">(Optional)</span>
                            </label>
                            
                            <div
                                @drop.prevent="handleImageDrop"
                                @dragover.prevent="isDragging = true"
                                @dragleave.prevent="isDragging = false"
                                @click="triggerImageInput"
                                :class="[
                                    'tw-relative tw-w-full tw-h-64 tw-border-2 tw-border-dashed tw-rounded-lg tw-flex tw-flex-col tw-items-center tw-justify-center tw-cursor-pointer tw-transition-all tw-duration-200',
                                    isDragging ? 'tw-border-primary-500 tw-bg-primary-50' : 'tw-border-gray-300 hover:tw-border-primary-400',
                                    productForm.imagePreview ? 'tw-border-solid' : ''
                                ]"
                            >
                                <!-- Image Preview -->
                                <div v-if="productForm.imagePreview" class="tw-relative tw-w-full tw-h-full">
                                    <img
                                        :src="productForm.imagePreview"
                                        alt="Product preview"
                                        class="tw-w-full tw-h-full tw-object-cover tw-rounded-lg"
                                    />
                                    <button
                                        @click.stop="removeImage"
                                        type="button"
                                        class="tw-absolute tw-top-2 tw-right-2 tw-w-8 tw-h-8 tw-bg-red-500 tw-text-white tw-rounded-full tw-flex tw-items-center tw-justify-center hover:tw-bg-red-600 tw-transition-colors"
                                    >
                                        <icon icon="solar:close-circle-bold" class="tw-text-lg" />
                                    </button>
                                </div>
                                
                                <!-- Upload Placeholder -->
                                <div v-else class="tw-text-center">
                                    <icon icon="solar:camera-add-bold-duotone" class="tw-text-6xl tw-text-gray-400 tw-mb-4 tw-mx-auto" />
                                    <p class="tw-text-lg tw-font-medium tw-text-gray-700 tw-mb-2">
                                        Drop an image here or click to upload
                                    </p>
                                    <p class="tw-text-sm tw-text-gray-500 tw-mb-4">
                                        Supports JPG, PNG, GIF files up to 5MB
                                    </p>
                                    <div class="tw-inline-flex tw-items-center tw-px-4 tw-py-2 tw-bg-primary-100 tw-text-primary-700 tw-rounded-md tw-text-sm tw-font-medium">
                                        <icon icon="solar:upload-bold-duotone" class="tw-mr-2" />
                                        Choose Image
                                    </div>
                                </div>
                                
                                <input
                                    ref="imageInput"
                                    type="file"
                                    accept="image/*"
                                    @change="handleImageSelect"
                                    class="tw-hidden"
                                />
                            </div>
                            
                            <p class="tw-text-xs tw-text-gray-500 tw-mt-2">
                                Recommended size: 800x600px. Maximum file size: 5MB
                            </p>
                        </div>

                        <!-- Image Upload Tips -->
                        <div class="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-p-4">
                            <h3 class="tw-text-sm tw-font-medium tw-text-blue-900 tw-mb-2">
                                <icon icon="solar:info-circle-bold" class="tw-mr-1" />
                                Image Tips
                            </h3>
                            <ul class="tw-text-sm tw-text-blue-800 tw-space-y-1">
                                <li>• Use high-quality images for better presentation</li>
                                <li>• Square or landscape orientation works best</li>
                                <li>• Ensure good lighting and clear product visibility</li>
                                <li>• Avoid images with heavy text overlays</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Form Actions -->
            <div class="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-pt-8 tw-mt-8 tw-border-t tw-border-gray-200">
                <div class="tw-flex tw-items-center tw-gap-3">
                    <!-- Save as Draft (for future use) -->
                    <a-button
                        v-if="false"
                        type="button"
                        intent="white"
                        @click="saveDraft"
                        :disabled="savingProduct"
                        left-icon="solar:bookmark-linear"
                    >
                        Save as Draft
                    </a-button>
                </div>

                <div class="tw-flex tw-items-center tw-gap-3">
                    <!-- Cancel Button -->
                    <a-button
                        type="button"
                        intent="white"
                        @click="goBack"
                        :disabled="savingProduct"
                    >
                        Cancel
                    </a-button>
                    
                    <!-- Create Product Button -->
                    <a-button
                        type="submit"
                        intent="primary"
                        :loading="savingProduct"
                        :disabled="!productForm.title || savingProduct"
                        left-icon="solar:add-circle-bold-duotone"
                        class="tw-min-w-[180px]"
                    >
                        Create Product
                    </a-button>
                </div>
            </div>
        </form>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import FormField from '@/components/form/FormField.vue'
import FormMultiSelect from '@/components/form/FormMultiSelect.vue'
import useAlert from '@/composables/useAlert'
import { create } from '@/api/products'
import appApi from '@/api/app'

const router = useRouter()

// Form state
const isDragging = ref(false)
const imageInput = ref(null)
const savingProduct = ref(false)

// Categories state
const categoriesLoading = ref(false)
const categories = ref([])

// Product form
const productForm = reactive({
    title: '',
    description: '',
    image: null,
    imagePreview: '',
    categories: [],
    price: '',
    sku: ''
})

const productErrors = reactive({
    title: '',
    description: '',
    categories: '',
    price: '',
    sku: ''
})

// Computed
const categoryOptions = computed(() => {
    return categories.value.map(cat => ({
        id: cat.id,
        name: cat.name
    }))
})

// Methods
const fetchCategories = async () => {
    categoriesLoading.value = true
    
    try {
        const response = await appApi.forms.categories('product')
        categories.value = response.data || []
    } catch (error) {
        console.error('Error fetching categories:', error)
        useAlert('Failed to load categories. Please try again.', 'warning')
        categories.value = []
    } finally {
        categoriesLoading.value = false
    }
}

const goBack = () => {
    router.push({ name: 'pages.company.products' })
}

const triggerImageInput = () => {
    imageInput.value?.click()
}

const handleImageSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
        processSelectedImage(file)
    }
}

const handleImageDrop = (event) => {
    isDragging.value = false
    const file = event.dataTransfer.files[0]
    if (file && file.type.startsWith('image/')) {
        processSelectedImage(file)
    }
}

const processSelectedImage = (file) => {
    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
        useAlert('Image file size must be less than 5MB', 'warning')
        return
    }

    productForm.image = file
    
    // Create preview
    const reader = new FileReader()
    reader.onload = (e) => {
        productForm.imagePreview = e.target.result
    }
    reader.readAsDataURL(file)
}

const removeImage = () => {
    productForm.image = null
    productForm.imagePreview = ''
    if (imageInput.value) {
        imageInput.value.value = ''
    }
}

const validateProductForm = () => {
    // Reset errors
    Object.keys(productErrors).forEach(key => {
        productErrors[key] = ''
    })

    let isValid = true

    // Title validation
    if (!productForm.title.trim()) {
        productErrors.title = 'Product title is required'
        isValid = false
    } else if (productForm.title.trim().length < 3) {
        productErrors.title = 'Product title must be at least 3 characters'
        isValid = false
    } else if (productForm.title.trim().length > 100) {
        productErrors.title = 'Product title must be less than 100 characters'
        isValid = false
    }

    // Description validation
    if (productForm.description.trim().length > 1000) {
        productErrors.description = 'Description must be less than 1000 characters'
        isValid = false
    }

    // Price validation (if enabled)
    if (productForm.price && (isNaN(productForm.price) || productForm.price < 0)) {
        productErrors.price = 'Price must be a valid positive number'
        isValid = false
    }

    return isValid
}

const saveProduct = async () => {
    if (!validateProductForm()) {
        useAlert('Please fix the form errors', 'warning')
        return
    }

    savingProduct.value = true

    try {

        const formData = new FormData()
        formData.append('name', productForm.title.trim())
        formData.append('description', productForm.description.trim())
        formData.append('thumbnail', productForm.image)
        formData.append('category_ids', JSON.stringify(productForm.categories.map(category => category.id)))


        await create(formData)
        .then(response => {
            if(response.status === 201) {
                useAlert('Product created successfully!', 'success')
                router.push({ name: 'pages.company.products' })
            }
        })
        .catch(error => {
            useAlert('Error creating product. Please try again.', 'danger')
            console.log(error)
        })


    } catch (error) {
        console.error('Error creating product:', error)
        useAlert('Error creating product. Please try again.', 'danger')
    } finally {
        savingProduct.value = false
    }
}

const saveDraft = async () => {
    savingProduct.value = true
    
    try {
        // Simulate saving draft
        await new Promise(resolve => setTimeout(resolve, 1000))
        useAlert('Product saved as draft', 'success')
    } catch (error) {
        useAlert('Error saving draft', 'danger')
    } finally {
        savingProduct.value = false
    }
}

// Initialize
onMounted(() => {
    fetchCategories()
})
</script>

<style scoped>
/* Additional custom styles if needed */
</style> 